import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { CSSProperties, ForwardedRef, ReactNode, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Card } from '../Card';
import { IconButton } from '../IconButton';
import { LayoutStack } from '../layouts';
import { LoadingSkeleton } from '../LoadingSkeleton';
import { TextLink } from '../TextLink';
import styles from './DonationListItem.module.css';
import { ListDonationListItemProps, ListDonationProps, ListSkeletonProps } from './types';
import useHeightObserver from './useHeightObserver';
function SkeletonInner() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <LoadingSkeleton width="6.25rem" height="3rem" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <>{t0}<LayoutStack space="200"><LoadingSkeleton width="5.7rem" height="1.3rem" /><LoadingSkeleton width="5.7rem" height="1.1rem" /></LayoutStack></>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function SkeletonWithoutHeightChange(t0) {
  const $ = _c(5);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = classNames(styles.card, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SkeletonInner />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <Card className={t1}>{t2}</Card>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
const SkeletonWithHeightChange = forwardRef(function SkeletonWithHeightObserver(t0, ref) {
  const $ = _c(11);
  const {
    className,
    style
  } = t0;
  const t1 = style as CSSProperties;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      ...t1,
      overflow: "visible"
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== className) {
    t3 = classNames(styles.card, className);
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SkeletonInner />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3) {
    t5 = <Card className={t3}>{t4}</Card>;
    $[5] = t3;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== ref || $[8] !== t2 || $[9] !== t5) {
    t6 = <div style={t2} ref={ref}>{t5}</div>;
    $[7] = ref;
    $[8] = t2;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
});
const Skeleton = forwardRef(function Skeleton(props, ref) {
  const $ = _c(5);
  if (props.onChangeSize) {
    let t0;
    if ($[0] !== props || $[1] !== ref) {
      t0 = <SkeletonWithHeightChange ref={ref} {...props} />;
      $[0] = props;
      $[1] = ref;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return t0;
  }
  let t0;
  if ($[3] !== props) {
    t0 = <SkeletonWithoutHeightChange {...props} />;
    $[3] = props;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return t0;
});
function Cta(t0) {
  const $ = _c(12);
  const {
    className,
    amount,
    ctaText,
    ctaLink
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = classNames(styles.card, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = classNames(styles.amountContainer, styles.ctaAmountContainer);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== amount) {
    t3 = <div className={t2}>{amount}</div>;
    $[3] = amount;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== ctaLink || $[6] !== ctaText) {
    t4 = <div className={styles.ctaContainer}><TextLink href={ctaLink}>{ctaText}</TextLink></div>;
    $[5] = ctaLink;
    $[6] = ctaText;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t3 || $[10] !== t4) {
    t5 = <Card className={t1}>{t3}{t4}</Card>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function Comment({
  comment,
  showMoreLabel,
  showLessLabel,
  maxPreviewLines = 2
}: {
  comment: string;
  showMoreLabel: string;
  showLessLabel: string;
  maxPreviewLines?: number;
}) {
  const [showMore, setShowMore] = useState(false);
  const [isTooLong, setIsTooLong] = useState(false);
  const label = showMore ? showLessLabel : showMoreLabel;
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
    const neededLines = Math.ceil(element.scrollHeight / lineHeight);
    setIsTooLong(neededLines > maxPreviewLines);
  }, [maxPreviewLines, setIsTooLong]);
  const toggleShowMore = useCallback(() => {
    setShowMore(prev => !prev);
  }, []);
  return <div className={styles.commentContainer}>
      <div ref={ref} className={classNames(styles.comment, {
      [styles.expanded!]: showMore
    })} style={{
      '--max-preview-lines': maxPreviewLines
    } as CSSProperties}>
        {comment}
      </div>
      {isTooLong && <IconButton kind="plain" size="small" iconName={showMore ? 'chevronUp' : 'chevronDown'} iconPosition="right" onClick={toggleShowMore} className={styles.showMoreButton}>
          {label}
        </IconButton>}
    </div>;
}
function Timestamp(t0) {
  const $ = _c(3);
  const {
    timestamp
  } = t0;
  if (!timestamp) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <LoadingSkeleton width="5.7rem" height="1.2615rem" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== timestamp) {
    t1 = <div className={styles.timestamp}>{timestamp}</div>;
    $[1] = timestamp;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function DonationListItemInner(t0) {
  const $ = _c(18);
  const {
    amount,
    heading,
    timestamp,
    comment,
    showMoreLabel,
    showLessLabel,
    maxPreviewLines
  } = t0;
  let t1;
  if ($[0] !== amount) {
    t1 = <div className={styles.amountContainer}><div>{amount}</div></div>;
    $[0] = amount;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== heading) {
    t2 = <div className={styles.heading}>{heading}</div>;
    $[2] = heading;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== timestamp) {
    t3 = <Timestamp timestamp={timestamp} />;
    $[4] = timestamp;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== comment || $[7] !== maxPreviewLines || $[8] !== showLessLabel || $[9] !== showMoreLabel) {
    t4 = comment && <Comment comment={comment} showMoreLabel={showMoreLabel} showLessLabel={showLessLabel} maxPreviewLines={maxPreviewLines} />;
    $[6] = comment;
    $[7] = maxPreviewLines;
    $[8] = showLessLabel;
    $[9] = showMoreLabel;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = <LayoutStack space="none">{t2}{t3}{t4}</LayoutStack>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t1 || $[16] !== t5) {
    t6 = <>{t1}{t5}</>;
    $[15] = t1;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}
function DonationListItemInnerWithoutHeightObserver(t0) {
  const $ = _c(10);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = classNames(styles.card, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props) {
    t2 = <DonationListItemInner {...props} />;
    $[5] = props;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <Card className={t1}>{t2}</Card>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
const DonationListItemWithHeightObserver = forwardRef(function DonationListItemWithHeightObserver(t0, ref) {
  const $ = _c(22);
  let className;
  let onChangeSize;
  let props;
  let style;
  if ($[0] !== t0) {
    ({
      className,
      style,
      onChangeSize,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onChangeSize;
    $[3] = props;
    $[4] = style;
  } else {
    className = $[1];
    onChangeSize = $[2];
    props = $[3];
    style = $[4];
  }
  let t1;
  if ($[5] !== onChangeSize || $[6] !== style) {
    t1 = {
      style,
      onChangeSize
    };
    $[5] = onChangeSize;
    $[6] = style;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const localRef = useHeightObserver(t1);
  const t2 = style as CSSProperties;
  let t3;
  if ($[8] !== t2) {
    t3 = {
      ...t2,
      overflow: "visible"
    };
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== className) {
    t4 = classNames(styles.card, className);
    $[10] = className;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== props) {
    t5 = <DonationListItemInner {...props} />;
    $[12] = props;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== localRef || $[15] !== t4 || $[16] !== t5) {
    t6 = <Card className={t4} ref={localRef}>{t5}</Card>;
    $[14] = localRef;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== ref || $[19] !== t3 || $[20] !== t6) {
    t7 = <div style={t3} ref={ref}>{t6}</div>;
    $[18] = ref;
    $[19] = t3;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
});
const DefaultDonationListItem = forwardRef(function DefaultDonationListItem(props, ref) {
  const $ = _c(5);
  if (props.onChangeSize) {
    let t0;
    if ($[0] !== props || $[1] !== ref) {
      t0 = <DonationListItemWithHeightObserver ref={ref} {...props} />;
      $[0] = props;
      $[1] = ref;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return t0;
  }
  let t0;
  if ($[3] !== props) {
    t0 = <DonationListItemInnerWithoutHeightObserver {...props} />;
    $[3] = props;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return t0;
});
const DonationListItem = forwardRef(function DonationListItem(t0, ref) {
  const $ = _c(25);
  let amount;
  let className;
  let ctaLink;
  let ctaText;
  let props;
  let variant;
  if ($[0] !== t0) {
    ({
      variant,
      amount,
      ctaText,
      ctaLink,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = amount;
    $[2] = className;
    $[3] = ctaLink;
    $[4] = ctaText;
    $[5] = props;
    $[6] = variant;
  } else {
    amount = $[1];
    className = $[2];
    ctaLink = $[3];
    ctaText = $[4];
    props = $[5];
    variant = $[6];
  }
  if (variant === "cta") {
    let t1;
    if ($[7] !== amount || $[8] !== className || $[9] !== ctaLink || $[10] !== ctaText) {
      t1 = <Cta amount={amount} ctaText={ctaText} ctaLink={ctaLink} className={className} />;
      $[7] = amount;
      $[8] = className;
      $[9] = ctaLink;
      $[10] = ctaText;
      $[11] = t1;
    } else {
      t1 = $[11];
    }
    return t1;
  }
  if (variant === "skeleton") {
    const t1 = props as ListSkeletonProps;
    let t2;
    if ($[12] !== className || $[13] !== ref || $[14] !== t1) {
      t2 = <Skeleton ref={ref} className={className} {...t1} />;
      $[12] = className;
      $[13] = ref;
      $[14] = t1;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    return t2;
  }
  let t1;
  if ($[16] !== amount || $[17] !== className || $[18] !== ctaLink || $[19] !== ctaText || $[20] !== props || $[21] !== variant) {
    t1 = {
      variant,
      amount,
      ctaText,
      ctaLink,
      className,
      ...props
    };
    $[16] = amount;
    $[17] = className;
    $[18] = ctaLink;
    $[19] = ctaText;
    $[20] = props;
    $[21] = variant;
    $[22] = t1;
  } else {
    t1 = $[22];
  }
  const donationProps = t1 as ListDonationProps;
  let t2;
  if ($[23] !== donationProps) {
    t2 = <DefaultDonationListItem {...donationProps} />;
    $[23] = donationProps;
    $[24] = t2;
  } else {
    t2 = $[24];
  }
  return t2;
});
export default DonationListItem;